import React, { useId, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Progressive disclosure for long listings.
//
// Several pages render every item in a dataset inline (all parishes, all
// scholarships, every exam subject, the full FAQ), which pushes the page to
// many screens of scroll and buries whatever follows the list. These two
// components cap the initial render and put the rest behind an explicit
// control.
//
// SEO: the collapsed remainder is toggled with `hidden` (display:none) rather
// than being unmounted, so it is present in the served HTML and in the
// prerendered output. Google indexes content behind expand/collapse controls;
// keeping it display:none (instead of clipped-but-visible) is also what keeps
// links in the hidden region out of the tab order, so there is no invisible
// keyboard trap.
//
// A11y: the toggle owns the region via aria-controls/aria-expanded, the label
// says how many items remain rather than a bare "Read more", and the control
// clears a 44px touch target.

const Toggle = ({ expanded, onClick, controls, moreLabel, lessLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={expanded}
    aria-controls={controls}
    className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[44px] px-5 py-3 rounded-xl text-[15px] font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-100 transition-all duration-150 hover:bg-brand-100 hover:ring-brand-200 active:scale-[0.97]"
  >
    {expanded ? lessLabel : moreLabel}
    {expanded ? (
      <ChevronUp className="w-4 h-4" aria-hidden="true" />
    ) : (
      <ChevronDown className="w-4 h-4" aria-hidden="true" />
    )}
  </button>
);

/**
 * Caps a list at `initial` items and hides the rest behind a toggle.
 *
 * <ShowMoreList items={scholarships} initial={6} noun="scholarships">
 *   {(item) => <ScholarshipCard key={item.id} {...item} />}
 * </ShowMoreList>
 *
 * `className` is applied to the wrapper of BOTH the visible and hidden groups,
 * so grid/space utilities keep working across the split.
 */
export const ShowMoreList = ({
  items = [],
  initial = 6,
  noun = 'items',
  className = '',
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();

  if (!Array.isArray(items) || items.length === 0) return null;

  const visible = items.slice(0, initial);
  const hidden = items.slice(initial);
  const renderItem = typeof children === 'function' ? children : () => null;

  return (
    <div>
      <div className={className}>{visible.map((item, i) => renderItem(item, i))}</div>

      {hidden.length > 0 && (
        <>
          {/* The layout classes are applied ONLY when expanded. The `hidden`
              attribute works by setting `display:none` in the UA stylesheet,
              which any explicit display utility — `grid`, `flex` — silently
              overrides, so passing `className` through while collapsed leaves
              the "hidden" items fully visible. */}
          <div
            id={regionId}
            hidden={!expanded}
            className={expanded ? `${className} mt-4` : undefined}
          >
            {hidden.map((item, i) => renderItem(item, i + initial))}
          </div>
          <Toggle
            expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            controls={regionId}
            moreLabel={`Show ${hidden.length} more ${noun}`}
            lessLabel={`Show fewer ${noun}`}
          />
        </>
      )}
    </div>
  );
};

/**
 * Clips arbitrary content (prose, a nested table, a long card body) to a
 * maximum height with a fade, and expands on demand. Use this where the
 * content is not a uniform list that can be sliced.
 *
 * Unlike ShowMoreList this keeps the content rendered and visible-but-clipped,
 * so it is only appropriate for blocks WITHOUT interactive elements below the
 * fold — otherwise those controls would be reachable by keyboard while
 * visually cut off.
 */
export const ShowMore = ({
  collapsedHeight = 200,
  moreLabel = 'Read more',
  lessLabel = 'Show less',
  className = '',
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();

  return (
    <div className={className}>
      <div
        id={regionId}
        className={`relative overflow-hidden transition-[max-height] duration-300 ease-out ${
          expanded ? '' : 'fade-to-canvas'
        }`}
        style={{ maxHeight: expanded ? 'none' : collapsedHeight }}
      >
        {children}
      </div>
      <Toggle
        expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        controls={regionId}
        moreLabel={moreLabel}
        lessLabel={lessLabel}
      />
    </div>
  );
};

export default ShowMoreList;
